import { useCallback, useMemo, useState } from "react";

import { Button, Icon } from "@nexus/ui";
import { TASK_NAVIGATION_SECTION } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

import { exportPersonaOpportunitiesCsv } from "../../../lib/export-persona-opportunities.js";
import { PERSONA_STAGE_RAIL_GAP_CLASS } from "../../../lib/persona-opportunity-stage-chrome.js";
import {
  filterPersonaOpportunityOpportunitiesModel,
  flattenPersonaOpportunityRows,
  hasActivePersonaOpportunityOpportunitiesFilters,
} from "../../../lib/persona-opportunity-opportunities-filters.js";
import { toPersonaOpportunityOpportunitiesModel } from "../../../lib/persona-opportunity-row-model.js";
import { PersonaOpportunitiesStatusTabs } from "../PersonaOpportunitiesStatusTabs/PersonaOpportunitiesStatusTabs.js";
import { PersonaOpportunitySetHeader } from "../PersonaOpportunitySetHeader/PersonaOpportunitySetHeader.js";
import { OpportunitiesTab } from "../tabs/OpportunitiesTab/OpportunitiesTab.js";
import { ScriptsTab } from "../tabs/ScriptsTab/ScriptsTab.js";
import { SentToVendorTabPlaceholder } from "../tabs/SentToVendorTab/SentToVendorTabPlaceholder.js";
import type { PersonaOpportunitySetDetail } from "../../../api/persona-opportunity-sets.api.js";
import {
  EMPTY_PERSONA_OPPORTUNITY_TAB_COUNTS,
  type PersonaOpportunitySet,
  type PersonaOpportunityStatusFilter,
  type PersonaOpportunityTabKey,
  type PersonaOpportunityViewMode,
} from "../../../types/persona-opportunities.types.js";

export interface PersonaOpportunitySetWorkspaceProps {
  selectedSet: PersonaOpportunitySet;
  sets: PersonaOpportunitySet[];
  detail?: PersonaOpportunitySetDetail;
  isDetailLoading?: boolean;
  activeTab: PersonaOpportunityTabKey;
  view: PersonaOpportunityViewMode;
  canCreateSet?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: PersonaOpportunityStatusFilter;
  onStatusFilterChange: (value: PersonaOpportunityStatusFilter) => void;
  onTabChange: (tab: PersonaOpportunityTabKey) => void;
  onViewChange: (view: PersonaOpportunityViewMode) => void;
  onBack: () => void;
  onSelectSet: (set: PersonaOpportunitySet) => void;
  onCreateSet?: () => void;
  onRegenerate?: () => void;
  onRetrySet?: (set: PersonaOpportunitySet) => void;
}

export function PersonaOpportunitySetWorkspace({
  selectedSet,
  sets,
  detail,
  isDetailLoading = false,
  activeTab,
  view,
  canCreateSet = false,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onTabChange,
  onViewChange,
  onBack,
  onSelectSet,
  onCreateSet,
  onRegenerate,
  onRetrySet,
}: PersonaOpportunitySetWorkspaceProps) {
  const { t } = useTranslation();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const isGenerating = selectedSet.status === "GENERATING";
  const isFailed = selectedSet.status === "FAILED";

  const exportFilters = useMemo(
    () => ({ search, status: statusFilter }),
    [search, statusFilter],
  );
  const exportFiltersActive = hasActivePersonaOpportunityOpportunitiesFilters(exportFilters);
  const exportModel = useMemo(
    () => (detail ? toPersonaOpportunityOpportunitiesModel(detail, t) : null),
    [detail, t],
  );
  const filteredExportModel = useMemo(
    () => (exportModel ? filterPersonaOpportunityOpportunitiesModel(exportModel, exportFilters) : null),
    [exportModel, exportFilters],
  );

  const handleExportAll = useCallback(() => {
    if (!exportModel || !detail) return;
    exportPersonaOpportunitiesCsv(
      flattenPersonaOpportunityRows(exportModel.groups),
      `${detail.set.name.replace(/[^\w.-]+/g, "_")}-opportunities.csv`,
    );
  }, [detail, exportModel]);

  const handleExportFiltered = useCallback(() => {
    if (!filteredExportModel || !detail) return;
    exportPersonaOpportunitiesCsv(
      flattenPersonaOpportunityRows(filteredExportModel.groups),
      `${detail.set.name.replace(/[^\w.-]+/g, "_")}-opportunities-filtered.csv`,
    );
  }, [detail, filteredExportModel]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PersonaOpportunitySetHeader
        selectedSet={selectedSet}
        sets={sets}
        canCreateSet={canCreateSet}
        onBack={onBack}
        onSelectSet={onSelectSet}
        onCreateSet={onCreateSet}
        onRegenerate={onRegenerate}
        canImport={activeTab === "scripts" && canCreateSet}
        onImport={() => setIsImportOpen(true)}
        exportFiltersActive={exportFiltersActive}
        onExportAll={handleExportAll}
        onExportFiltered={handleExportFiltered}
      />

      {isGenerating ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm text-foreground"
        >
          <Icon name="loader" size="sm" className="mt-0.5 animate-spin text-primary" />
          <div className="min-w-0">
            <p className="font-medium">
              {t("personaOpportunities.workspace.generatingBannerTitle", "Generating opportunities…")}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t(
                "personaOpportunities.workspace.generatingBannerDescription",
                "This set is still running. You can leave this page — progress continues in the background.",
              )}
            </p>
          </div>
        </div>
      ) : null}

      {isFailed ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-foreground"
        >
          <Icon name="alert-circle" size="sm" className="mt-0.5 text-destructive" />
          <div className="min-w-0">
            <p className="font-medium text-destructive">
              {t("personaOpportunities.workspace.failedBannerTitle", "Generation failed")}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t(
                "personaOpportunities.workspace.failedBannerDescription",
                "Something went wrong while generating this set. Use Retry on the set card, create a new set, or contact support if it keeps failing.",
              )}
            </p>
            {canCreateSet && onRetrySet ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => onRetrySet(selectedSet)}
              >
                {t("common.retry", "Retry")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <PersonaOpportunitiesStatusTabs
        counts={selectedSet.counts ?? EMPTY_PERSONA_OPPORTUNITY_TAB_COUNTS}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      <div
        data-task-section={
          activeTab === "scripts" ? TASK_NAVIGATION_SECTION.PERSONA_SCRIPTS : undefined
        }
        className={`flex min-h-0 flex-1 scroll-mt-6 flex-col overflow-hidden ${PERSONA_STAGE_RAIL_GAP_CLASS}`}
      >
        {activeTab === "opportunities" ? (
          <OpportunitiesTab
            detail={detail}
            isDetailLoading={isDetailLoading}
            view={view}
            onViewChange={onViewChange}
            search={search}
            onSearchChange={onSearchChange}
            status={statusFilter}
            onStatusChange={onStatusFilterChange}
            canGenerateScripts={canCreateSet}
            isGenerating={isGenerating}
            onGoToScripts={() => onTabChange("scripts")}
          />
        ) : null}
        {activeTab === "scripts" ? (
          <ScriptsTab
            detail={detail}
            isDetailLoading={isDetailLoading}
            canGenerateScripts={!!canCreateSet}
            importOpen={isImportOpen}
            onImportOpenChange={setIsImportOpen}
            onGoToOpportunities={() => onTabChange("opportunities")}
          />
        ) : null}
        {activeTab === "sent_to_vendor" ? (
          <SentToVendorTabPlaceholder onGoToOpportunities={() => onTabChange("opportunities")} />
        ) : null}
      </div>
      </div>
    </div>
  );
}
